import { memo } from "react";
import {
  Plus,
  CaretRight,
  GearSix,
  UserPlus,
  UsersThree,
  GlobeSimple,
} from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { cn } from "@/lib/utils";
import type { SpaceActions } from "./use-space-actions";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpace = any;

interface SpaceRowProps {
  space: AnySpace;
  isExpanded: boolean;
  isWorkspaceAdmin: boolean;
  /** True when the viewer has a current workspace context; controls
   *  the visibility of the "invite to space" admin button. */
  hasWorkspaceContext: boolean;
  actions: SpaceActions;
}

export const SpaceRow = memo(function SpaceRow({
  space,
  isExpanded,
  isWorkspaceAdmin,
  hasWorkspaceContext,
  actions,
}: SpaceRowProps) {
  return (
    <div className="group/space flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-[14px] text-foreground hover:bg-foreground/10">
      <Button
        type="button"
        variant="ghost"
        onClick={() => actions.toggleExpanded(space.id)}
        className="h-auto min-w-0 flex-1 justify-start gap-1.5 overflow-hidden px-0 py-0 text-left hover:bg-transparent"
      >
        {space._isLinked ? (
          <GlobeSimple size={14} className="shrink-0 text-muted-foreground" />
        ) : (
          <UsersThree size={14} className="shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 truncate">{space.name}</span>
        <CaretRight
          size={8}
          weight="Bold"
          className={cn(
            "shrink-0 text-muted-foreground/50 transition-transform duration-150",
            isExpanded && "rotate-90",
          )}
        />
      </Button>
      {(isWorkspaceAdmin || space._isLinked) && (
        <IconTooltip label="Create channel in space">
          <ToolbarIconButton
            onClick={(e) => {
              e.stopPropagation();
              actions.openCreateChannel(space);
            }}
            aria-label="Create channel in space"
            className="shrink-0 opacity-0 group-hover/space:opacity-100"
          >
            <Plus size={12} className="text-muted-foreground" />
          </ToolbarIconButton>
        </IconTooltip>
      )}
      {isWorkspaceAdmin && hasWorkspaceContext && (
        <IconTooltip label="Invite to space">
          <ToolbarIconButton
            onClick={(e) => {
              e.stopPropagation();
              actions.openInvite(space);
            }}
            aria-label="Invite to space"
            className="shrink-0 opacity-0 group-hover/space:opacity-100"
          >
            <UserPlus size={12} className="text-muted-foreground" />
          </ToolbarIconButton>
        </IconTooltip>
      )}
      {isWorkspaceAdmin && (
        <IconTooltip label="Space settings">
          <ToolbarIconButton
            onClick={(e) => {
              e.stopPropagation();
              actions.openSettings(space.id);
            }}
            aria-label="Space settings"
            className="shrink-0 opacity-0 group-hover/space:opacity-100"
          >
            <GearSix size={12} className="text-muted-foreground" />
          </ToolbarIconButton>
        </IconTooltip>
      )}
    </div>
  );
});
